import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  cargarPreguntasPorTipo,
  calcularPuntuacionExamen,
  guardarPreguntaFallada
} from '../utils/preguntasService';
import TrialService from '../services/auth/TrialService';
import AuthService from '../services/auth/AuthService';
import { ErrorTypes, createErrorWithType } from '../utils/errorHandling';

/**
 * Custom hook for managing test logic
 * @param {Object} options - Configuration options
 * @param {string} options.type - Test type (viajeros, mercancias)
 * @param {boolean} options.isPracticeMode - Whether this is practice mode
 * @param {number} options.questionCount - Number of questions to load
 * @param {number} options.correctAnswerDelay - Delay after correct answer (practice mode)
 * @param {number} options.incorrectAnswerDelay - Delay after incorrect answer (practice mode)
 * @param {boolean} options.enabled - Whether to enable the hook (default: true)
 */
const useTestLogic = ({
  type,
  isPracticeMode = false,
  questionCount = 30,
  correctAnswerDelay = 500,
  incorrectAnswerDelay = 5000,
  enabled = true
}) => {
  const [preguntas, setPreguntas] = useState([]);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState({});
  const [testCompletado, setTestCompletado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarExplicacion, setMostrarExplicacion] = useState(false);
  const [autoNavigationTimer, setAutoNavigationTimer] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const navigate = useNavigate();
  const { isAuthenticated, getToken } = useAuth();

  // Clean up timers when component unmounts
  useEffect(() => {
    return () => {
      if (autoNavigationTimer) {
        clearTimeout(autoNavigationTimer);
      }
    };
  }, [autoNavigationTimer]);

  // Initialize test by loading questions
  const inicializarTest = useCallback(async () => {
    try {
      console.log(`Iniciando carga de preguntas ${isPracticeMode ? 'práctica' : 'aleatorias'}...`);
      
      // Get current token if authenticated
      const currentToken = isAuthenticated ? await getToken() : null;
      
      // Add a random seed to ensure questions are randomized each time
      // This ensures that when a user abandons a test and comes back, they get different questions
      const randomSeed = Date.now().toString();
      
      // Cargar preguntas aleatorias del tipo seleccionado
      const preguntasAleatorias = await cargarPreguntasPorTipo(type, null, currentToken, questionCount, randomSeed);
      console.log(`Preguntas ${isPracticeMode ? 'práctica' : 'aleatorias'} cargadas:`, preguntasAleatorias.length);

      if (!preguntasAleatorias || preguntasAleatorias.length === 0) {
        throw new Error('No se pudieron cargar las preguntas');
      }

      if (preguntasAleatorias.length !== questionCount) {
        throw new Error(`Se requieren ${questionCount} preguntas para el test`);
      }

      // Only record test after questions are loaded successfully
      // We'll move this to the end of the test to avoid marking a test as taken
      // before the user actually completes it
      console.log('Questions loaded successfully, test can begin');
      
      setPreguntas(preguntasAleatorias);
      setCargando(false);
    } catch (err) {
      console.error(`Error al cargar preguntas ${isPracticeMode ? 'práctica' : 'aleatorias'}:`, err);
      
      // Check if this is an authentication error (trial limit)
      if (err.type === 'AUTH') {
        setError({
          type: 'AUTH',
          message: err.message
        });
      } else {
        setError({
          type: 'load',
          message: err.message === 'Failed to fetch'
            ? 'Error de conexión al cargar las preguntas. Por favor, verifica tu conexión a internet.'
            : err.message
        });
      }
      
      setCargando(false);
    }
  }, [isAuthenticated, getToken, type, questionCount, isPracticeMode]);

  // Check availability and initialize test
  useEffect(() => {
    // Skip if the hook is disabled
    if (!enabled) {
      setCargando(false);
      return;
    }
    
    let mounted = true;
    
    const checkAvailability = async () => {
      try {
        // Skip availability check for authenticated users
        if (isAuthenticated) {
          console.log('User is authenticated, skipping trial availability check');
          await inicializarTest();
          return;
        }

        console.log('User is not authenticated, checking trial availability');
        
        // Force refresh the status to ensure it's up-to-date
        const status = await TrialService.getUserStatus(true);
        if (!mounted) return;
        
        setTestStatus(status);

        // If user can't take test, show error
        if (status.testsAvailable === 0) {
          let errorMessage = 'Prueba gratuita no disponible.';
          
          if (status.nextTestTime) {
            errorMessage += ` Próximo test disponible en: ${TrialService.formatTimeUntilNextTest(status.nextTestTime)}`;
          }
            
          // Add more specific message if session limited
          if (status.sessionLimited) {
            errorMessage = 'Ya has realizado tu test gratuito diario. Vuelve mañana para realizar otro test.';
          }
          
          // Add specific message for IP-based limitation (incognito mode)
          if (status.ipLimited) {
            errorMessage = 'Esta dirección IP ya ha utilizado su prueba gratuita diaria. Cada usuario puede realizar 1 test gratuito al día.';
          }
          
          setError({
            type: ErrorTypes.TRIAL_LIMIT,
            message: errorMessage,
            nextTestTime: status.nextTestTime,
            ipLimited: status.ipLimited,
            sessionLimited: status.sessionLimited,
            trialStatus: status
          });
          setCargando(false);
          return;
        }

        // Continue with test initialization
        await inicializarTest();
      } catch (err) {
        if (mounted) {
          console.error('Error checking test availability:', err);
          setError({
            type: 'access',
            message: 'Error al verificar disponibilidad del test'
          });
          setCargando(false);
        }
      }
    };

    checkAvailability();
    
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, inicializarTest, enabled]);

  // Handle answer selection
  const handleRespuesta = useCallback((opcionIndex) => {
    if (!preguntas[preguntaActual]) return;

    if (autoNavigationTimer) {
      clearTimeout(autoNavigationTimer);
      setAutoNavigationTimer(null);
    }

    const preguntaActualObj = preguntas[preguntaActual];
    const esRevelada = opcionIndex === -1;
    const esCorrecta = !esRevelada && opcionIndex === preguntaActualObj.respuestaCorrecta;

    setRespuestas(prev => ({
      ...prev,
      [preguntaActual]: {
        seleccionada: opcionIndex,
        correcta: esCorrecta,
        revelada: esRevelada
      }
    }));

    if (!esCorrecta) {
      guardarPreguntaFallada(preguntaActualObj);
    }

    // In practice mode, show explanation and delay navigation
    if (isPracticeMode) {
      if (esRevelada) {
        setMostrarExplicacion(true);
        return;
      }

      if (esCorrecta) {
        const timer = setTimeout(() => {
          if (preguntaActual < preguntas.length - 1) {
            setPreguntaActual(prev => prev + 1);
            setMostrarExplicacion(false);
          } else {
            handleFinalizarTest();
          }
        }, correctAnswerDelay);
        setAutoNavigationTimer(timer);
      } else {
        setMostrarExplicacion(true);
        const timer = setTimeout(() => {
          if (preguntaActual < preguntas.length - 1) {
            setPreguntaActual(prev => prev + 1);
            setMostrarExplicacion(false);
          } else {
            handleFinalizarTest();
          }
        }, incorrectAnswerDelay);
        setAutoNavigationTimer(timer);
      }
    } else {
      // In random mode, immediately go to next question
      if (preguntaActual < preguntas.length - 1) {
        setPreguntaActual(prev => prev + 1);
      } else {
        handleFinalizarTest();
      }
    }
  }, [preguntaActual, preguntas, autoNavigationTimer, isPracticeMode, correctAnswerDelay, incorrectAnswerDelay]);

  // Handle navigation to previous question (practice mode only)
  const handlePreviousQuestion = useCallback(() => {
    if (!isPracticeMode) return;
    
    if (autoNavigationTimer) {
      clearTimeout(autoNavigationTimer);
      setAutoNavigationTimer(null);
    }
    
    if (preguntaActual > 0) {
      setPreguntaActual(prev => prev - 1);
      setMostrarExplicacion(false);
    }
  }, [preguntaActual, autoNavigationTimer, isPracticeMode]);

  // Handle navigation to next question (practice mode only)
  const handleNextQuestion = useCallback(() => {
    if (!isPracticeMode) return;
    
    if (autoNavigationTimer) {
      clearTimeout(autoNavigationTimer);
      setAutoNavigationTimer(null);
    }
    
    const hasAnswered = respuestas[preguntaActual]?.seleccionada !== undefined;
    
    if (hasAnswered && preguntaActual < preguntas.length - 1) {
      setPreguntaActual(prev => prev + 1);
      setMostrarExplicacion(false);
    } else if (hasAnswered && preguntaActual === preguntas.length - 1) {
      handleFinalizarTest();
    }
  }, [preguntaActual, respuestas, autoNavigationTimer, preguntas.length, isPracticeMode]);

  // Handle skipping a question
  const handleSaltar = useCallback(() => {
    if (autoNavigationTimer) {
      clearTimeout(autoNavigationTimer);
      setAutoNavigationTimer(null);
    }
    
    if (preguntaActual < preguntas.length - 1) {
      setPreguntaActual(prev => prev + 1);
      setMostrarExplicacion(false);
    } else {
      handleFinalizarTest();
    }
  }, [preguntaActual, preguntas.length, autoNavigationTimer]);

  // Handle finishing the test
  const handleFinalizarTest = useCallback(() => {
    if (autoNavigationTimer) {
      clearTimeout(autoNavigationTimer);
      setAutoNavigationTimer(null);
    }
    
    setTestCompletado(true);
    
    // Guardar preguntas falladas
    preguntas.forEach((pregunta, index) => {
      const respuesta = respuestas[index];
      if (respuesta && !respuesta.correcta) {
        guardarPreguntaFallada(pregunta);
      }
    });
  }, [preguntas, respuestas, autoNavigationTimer]);

  // Handle revealing the answer (practice mode only)
  const handleRevelar = useCallback(() => {
    if (!isPracticeMode) return;
    
    if (autoNavigationTimer) {
      clearTimeout(autoNavigationTimer);
      setAutoNavigationTimer(null);
    }

    handleRespuesta(-1);
  }, [autoNavigationTimer, handleRespuesta, isPracticeMode]);

  // Handle confirming abandoning the test
  const handleConfirmAbandon = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Calculate test results
  const resultado = testCompletado ? calcularPuntuacionExamen(respuestas, preguntas) : null;

  return {
    // State
    preguntas,
    preguntaActual,
    respuestas,
    testCompletado,
    cargando,
    error,
    mostrarExplicacion,
    testStatus,
    showConfirmModal,
    
    // Computed values
    isPracticeMode,
    resultado,
    preguntaActualObj: preguntas[preguntaActual],
    respuestaActual: respuestas[preguntaActual],
    hasNextQuestion: preguntaActual < preguntas.length - 1,
    isLastQuestion: preguntaActual === preguntas.length - 1,
    
    // Actions
    setPreguntaActual,
    setShowConfirmModal,
    handleRespuesta,
    handlePreviousQuestion,
    handleNextQuestion,
    handleSaltar,
    handleFinalizarTest,
    handleRevelar,
    handleConfirmAbandon
  };
};

export default useTestLogic;